import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProbabilityCalculator = () => {
  const [inputData, setInputData] = useState({
    trials: '',
    successes: '',
    probability: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateProbability = () => {
    const { trials, successes, probability } = inputData;

    // Validate input
    if (!trials || !successes || !probability) {
      setError('Please fill in all fields');
      return;
    }

    const trialsNum = parseInt(trials, 10);
    const successesNum = parseInt(successes, 10);
    const probabilityNum = parseFloat(probability);

    if (isNaN(trialsNum) || isNaN(successesNum) || isNaN(probabilityNum)) {
      setError('Please enter valid numbers');
      return;
    }

    if (trialsNum < successesNum) {
      setError('Number of trials must be greater than or equal to number of successes');
      return;
    }

    if (probabilityNum < 0 || probabilityNum > 1) {
      setError('Probability must be between 0 and 1');
      return;
    }

    // Calculate binomial probability
    const binomialCoefficient = (n, k) => {
      let result = 1;
      for (let i = 0; i < k; i++) {
        result *= (n - i) / (i + 1);
      }
      return result;
    };

    const binomialProbability = binomialCoefficient(trialsNum, successesNum) * 
                                Math.pow(probabilityNum, successesNum) * 
                                Math.pow(1 - probabilityNum, trialsNum - successesNum);

    // Create visualization data
    const labels = Array.from({ length: trialsNum + 1 }, (_, i) => `k=${i}`);
    const data = labels.map(k => binomialCoefficient(trialsNum, parseInt(k.split('=')[1])) * 
                                 Math.pow(probabilityNum, parseInt(k.split('=')[1])) * 
                                 Math.pow(1 - probabilityNum, trialsNum - parseInt(k.split('=')[1])));

    const chartData = {
      labels,
      datasets: [{
        label: 'Binomial Probability',
        data,
        backgroundColor: data.map(() => 'rgba(75, 192, 192, 0.5)'),
        borderColor: data.map(() => 'rgba(75, 192, 192, 1)'),
        borderWidth: 1
      }]
    };

    setResults({
      binomialProbability,
      chartData
    });
  };

  const exampleData = {
    trials: '10',
    successes: '3',
    probability: '0.5'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Probability Calculator | Binomial Distribution Tool</title>
          <meta name="description" content="Calculate binomial probabilities for a given number of trials, successes, and probability of success. This calculator helps in statistical analysis and probability theory." />
          <meta name="keywords" content="probability calculator, binomial distribution, statistics, probability theory" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">📊</div>
            <h1 className="text-3xl font-bold text-gray-800">Probability Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate binomial probabilities for a given number of trials, successes, and probability of success. This calculator helps in statistical analysis and probability theory.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Trials:</label>
              <input
                type="number"
                name="trials"
                value={inputData.trials}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter number of trials"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Successes:</label>
              <input
                type="number"
                name="successes"
                value={inputData.successes}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter number of successes"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Probability of Success:</label>
              <input
                type="number"
                name="probability"
                value={inputData.probability}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter probability of success (0 to 1)"
              />
            </div>
          </div>

          <button
            onClick={calculateProbability}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Probability
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Binomial Probability</h3>
                  <p className="text-lg">P(X = {inputData.successes}) = {results.binomialProbability.toFixed(4)}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Probability Distribution</h3>
                  <p className="text-lg">Trials: {inputData.trials}</p>
                  <p className="text-lg">Probability of Success: {inputData.probability}</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Probability'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Number of Successes'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Binomial Probability Distribution'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for binomial probability:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Trials: {exampleData.trials}</li>
                <li>Successes: {exampleData.successes}</li>
                <li>Probability of Success: {exampleData.probability}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate the binomial coefficient (n choose k)</li>
                <li>Multiply by the probability of success raised to the number of successes</li>
                <li>Multiply by the probability of failure raised to the number of failures</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Probability Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Number of Trials:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the total number of trials</li>
                </ul>
              </li>
              <li><strong>Enter Number of Successes:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the number of successful trials</li>
                </ul>
              </li>
              <li><strong>Enter Probability of Success:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the probability of success for each trial (between 0 and 1)</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The binomial probability for the given number of successes</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated binomial probability</li>
                  <li>View the probability distribution visualization</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Binomial probability is useful for modeling binary outcomes</li>
                <li>Ensure the probability of success is between 0 and 1</li>
                <li>Useful for statistical analysis and probability theory</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Binomial Probability <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Binomial probability is used to calculate the probability of a specific number of successes in a fixed number of independent trials, each with the same probability of success. Key points:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Trials:</strong> The total number of independent trials</li>
              <li><strong>Successes:</strong> The number of successful trials</li>
              <li><strong>Probability of Success:</strong> The probability of success for each trial</li>
              <li><strong>Use Cases:</strong> Quality control, medical trials, market research</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Double-check your input values</li>
              <li><strong>Documentation:</strong> Record your calculations</li>
              <li><strong>Validation:</strong> Verify results with other tools or manual calculations</li>
              <li><strong>Implementation:</strong> Ensure the probability of success is accurate for your scenario</li>
            </ul>
            <p className="italic mt-4">
              Note: Always verify your calculations, especially when using probabilities for critical decision-making.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProbabilityCalculator;